<template>
  <div class="withdraw-page">
    <header class="withdraw-header">
      <button type="button" class="back-button" @click="$router.back()">
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ text.title }}</h1>
    </header>

    <main class="withdraw-main">
      <div class="history-row">
        <button
          type="button"
          class="text-button"
          @click="$router.push({ path: '/WithdrawRecord' })"
        >
          {{ text.history }}
          <van-icon name="arrow" />
        </button>
      </div>

      <section class="amount-card">
        <label for="withdraw-amount">{{ text.amount }}</label>

        <div class="amount-row">
          <span class="currency-symbol">{{ symbol }}</span>
          <input
            id="withdraw-amount"
            :value="withdraw_money"
            :disabled="!quote || submitting"
            :inputmode="currency === 'JPY' ? 'numeric' : 'decimal'"
            autocomplete="off"
            placeholder="0"
            @input="changeAmount"
          >
          <button
            type="button"
            class="text-button"
            :disabled="!ready || !quote || submitting"
            @click="allMoney"
          >
            {{ text.all }}
          </button>
        </div>

        <div class="balance-row">
          <span>{{ text.balance }}</span>
          <strong>{{ formatAmount(userInfo.money) }}</strong>
        </div>

        <div class="limits">
          <div>{{ text.minimum }}：{{ formatAmount(withdrawRole.min) }}</div>
          <div>{{ text.maximum }}：{{ formatAmount(withdrawRole.max) }}</div>
          <div>{{ text.daily }}：{{ withdrawRole.num == null ? '--' : withdrawRole.num }}</div>
        </div>

        <p v-if="!quote" class="notice">{{ text.rateUnavailable }}</p>
        <p v-if="!ready && !loading" class="notice">{{ text.loadFailed }}</p>
        
      </section>

      <van-button
        block
        class="submit-button"
        :loading="submitting"
        :disabled="!ready || !quote || submitting"
        @click="doWithdraw"
      >
        {{ text.submit }}
      </van-button>

      <button
        v-if="!ready && !loading"
        type="button"
        class="text-button retry-button"
        @click="loadPage"
      >
        {{ text.retry }}
      </button>
    </main>
  </div>
</template>

<script>
const TEXT = {
  'zh-CN': {
    title: '提现中心', history: '提现记录', amount: '提现金额（元）',
    all: '全部', balance: '余额', minimum: '单笔最低',
    maximum: '单笔最高', daily: '每日最高提现次数',
    submit: '申请提现', retry: '重新加载',
    invalid: '请输入有效金额，人民币最多保留两位小数',
    insufficient: '提现金额超过余额',
    limit: '提现金额不符合单笔限额',
    rateUnavailable: '汇率暂不可用，请稍后刷新页面重试',
    loadFailed: '账户或限额加载失败，请重新加载',
    failed: '提交未确认成功，请先查看提现记录，避免重复提交',
    success: '提现申请已提交',
    reference: ''
  },
  'ja-JP': {
    title: '出金', history: '出金履歴', amount: '出金額（円）',
    all: '全額', balance: '残高', minimum: '最低出金額',
    maximum: '最高出金額', daily: '1日の出金回数上限',
    submit: '出金を申請', retry: '再読み込み',
    invalid: '有効な金額を整数で入力してください',
    insufficient: '残高を超えています',
    limit: '出金限度額を確認してください',
    rateUnavailable: 'レートを取得できません。しばらくしてから再読み込みしてください',
    loadFailed: '口座情報または限度額を取得できませんでした',
    failed: '送信結果を確認できません。再送信する前に出金履歴をご確認ください',
    success: '出金申請を送信しました',
    reference: '円表示は参考換算額です。実際の受取通貨・金額は支払方法によって異なります。'
  },
  'en-US': {
    title: 'Withdraw', history: 'Withdrawal history',
    amount: 'Withdrawal amount ($)',
    all: 'All', balance: 'Balance', minimum: 'Minimum per withdrawal',
    maximum: 'Maximum per withdrawal', daily: 'Daily withdrawal limit (count)',
    submit: 'Request withdrawal', retry: 'Reload',
    invalid: 'Enter a valid amount with up to two decimal places',
    insufficient: 'The amount exceeds your balance',
    limit: 'The amount is outside the withdrawal limits',
    rateUnavailable: 'Rates are unavailable. Please reload the page later.',
    loadFailed: 'Unable to load your account or withdrawal limits.',
    failed: 'Submission could not be confirmed. Check withdrawal history before retrying.',
    success: 'Withdrawal request submitted',
    reference: 'Dollar amounts are reference conversions. The currency and amount received depend on the payout method.'
  }
};

function numeric(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function cnyCents(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
  const parts = raw.split('.');
  const result = Number(parts[0]) * 100 +
    Number(((parts[1] || '') + '00').slice(0, 2));
  return Number.isSafeInteger(result) ? result : null;
}

export default {
  data() {
    return {
      withdraw_money: '',
      userInfo: {},
      withdrawRole: {},
      quote: null,
      allCents: null,
      loading: false,
      submitting: false,
      ready: false
    };
  },
  computed: {
    text() {
      return TEXT[this.$i18n.locale] || TEXT['zh-CN'];
    },
    currency() {
      if (this.$i18n.locale === 'ja-JP') return 'JPY';
      if (this.$i18n.locale === 'en-US') return 'USD';
      return 'CNY';
    },
    symbol() {
      return this.currency === 'JPY' ? '円' :
        this.currency === 'USD' ? '$' : '元';
    },
    availableQuote() {
      return this.$withdrawQuote();
    }
  },
  watch: {
    availableQuote: {
      immediate: true,
      handler(value) {
        if (!this.withdraw_money && !this.submitting) {
          this.quote = value ? Object.assign({}, value) : null;
        }
      }
    },
    '$i18n.locale'() {
      this.withdraw_money = '';
      this.allCents = null;
      const value = this.$withdrawQuote();
      this.quote = value ? Object.assign({}, value) : null;
    }
  },
    methods: {
    withdrawalMessage(value) {
      const raw = String(value == null ? '' : value).trim();
      if (this.$i18n.locale === 'zh-CN') return raw || this.text.failed;

      const messages = {
        '当日提现次数已用完': [
          '本日の出金回数の上限に達しました',
          'You have reached the daily withdrawal limit.'
        ],
        '打码量不足': [
          '必要な取引量に達していません',
          'The required turnover has not been reached.'
        ],
        '余额不足': [
          '残高が不足しています',
          'Insufficient balance.'
        ],
        '请填写正确的金额': [
          '有効な金額を入力してください',
          'Enter a valid amount.'
        ],
        '鉴权错误': [
          '再度ログインしてください',
          'Please log in again.'
        ],
        '提现规则异常，请联系客服': [
          '出金設定に問題があります。サポートにお問い合わせください',
          'Withdrawal settings are unavailable. Contact support.'
        ],
        '扣款失败': [
          '残高の更新に失敗しました',
          'Unable to update your balance.'
        ],
        '提现记录保存失败': [
          '出金申請を保存できませんでした',
          'Unable to save the withdrawal request.'
        ],
        '提现失败，请稍后重试': [
          '出金申請に失敗しました。しばらくしてから再度お試しください',
          'Withdrawal request failed. Please try again later.'
        ]
      };

      const key = raw.replace(/[！!。]+$/, '');
      const pair = messages[key];
      if (pair) {
        return pair[this.$i18n.locale === 'ja-JP' ? 0 : 1];
      }

      if (/^提现金额不能(少于|大于)[：:]/.test(raw)) {
        return this.text.limit;
      }

      if (this.$te(raw)) return this.$t(raw);
      return this.text.failed;
    },
    changeAmount(event) {
      this.withdraw_money = event.target.value;
      this.allCents = null;
      if (!this.withdraw_money) {
        const value = this.$withdrawQuote();
        this.quote = value ? Object.assign({}, value) : null;
      }
    },
    formatAmount(value) {
      const n = numeric(value);
      if (n === null || !this.quote) return '--';
      const amount = n * this.quote.rate;
      if (!Number.isFinite(amount)) return '--';
      if (this.currency === 'CNY') return amount.toFixed(2) + '元';
      if (this.currency === 'JPY') {
        return amount.toLocaleString('ja-JP', {
          maximumFractionDigits: 0
        }) + '円';
      }
      return '$' + amount.toLocaleString('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    },
    async loadPage() {
      if (this.loading) return;
      this.loading = true;
      this.ready = false;
      try {
        const results = await Promise.all([
          this.$http({ method: 'get', url: 'user_info' }),
          this.$http({ method: 'get', url: 'user_get_withdraw_role' })
        ]);
        if (results.some(r => !r || Number(r.code) !== 200 || !r.data)) {
          throw new Error('LOAD_FAILED');
        }
        if (cnyCents(results[0].data.money) === null) {
          throw new Error('INVALID_BALANCE');
        }
        this.userInfo = results[0].data;
        this.withdrawRole = results[1].data;
        this.ready = true;
      } catch (error) {
        this.$toast(this.text.loadFailed);
      } finally {
        this.loading = false;
      }
    },
    allMoney() {
      if (!this.quote || !this.ready || this.submitting) return;
      const cents = cnyCents(this.userInfo.money);
      if (cents === null || cents <= 0) {
        this.$toast(this.text.insufficient);
        return;
      }
      const amount = cents / 100 * this.quote.rate;
      this.withdraw_money = amount.toFixed(this.currency === 'JPY' ? 0 : 2);
      this.allCents = cents;
    },
    async doWithdraw() {
      if (this.submitting || !this.ready) return;

      const current = this.$withdrawQuote();
      if (!this.quote || !current || current.currency !== this.quote.currency) {
        this.$toast(this.text.rateUnavailable);
        return;
      }

      const raw = this.withdraw_money.trim();
      const pattern = this.currency === 'JPY' ?
        /^\d+$/ : /^\d+(\.\d{1,2})?$/;
      const amount = Number(raw);
      if (!pattern.test(raw) || !Number.isFinite(amount) || amount <= 0) {
        this.$toast(this.text.invalid);
        return;
      }

      let cents = this.allCents;
      if (cents === null) {
        cents = this.currency === 'CNY' ? cnyCents(raw) :
          Math.floor(amount / this.quote.rate * 100 + 0.0000001);
      }
      if (!Number.isSafeInteger(cents) || cents <= 0) {
        this.$toast(this.text.invalid);
        return;
      }

      const balance = cnyCents(this.userInfo.money);
      if (balance === null || cents > balance) {
        this.$toast(this.text.insufficient);
        return;
      }

      const minimum = numeric(this.withdrawRole.min);
      const maximum = numeric(this.withdrawRole.max);
      if (minimum === null || maximum === null) {
        this.$toast(this.text.loadFailed);
        return;
      }

      const cny = cents / 100;
      if (cny < minimum || cny > maximum) {
        this.$toast(this.text.limit);
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$http({
          method: 'post',
          data: { money: cny.toFixed(2) },
          url: 'user_set_withdraw'
        });
        if (res && Number(res.code) === 200) {
          this.withdraw_money = '';
          this.allCents = null;
          this.$toast(this.text.success);
          this.$router.replace({ path: '/WithdrawRecord' });
        } else if (res && res.msg) {
                    this.$toast(this.withdrawalMessage(res.msg));
        } else {
          this.$toast(this.text.failed);
        }
      } catch (error) {
        this.$toast(this.text.failed);
      } finally {
        this.submitting = false;
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.replace({ path: '/Login' });
      return;
    }
    this.loadPage();
  }
};
</script>

<style scoped>
.withdraw-page {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  box-sizing: border-box;
  background: #282828;
  color: #f4f0eb;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
}
.withdraw-header {
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 16px;
  background: #202020;
}
.withdraw-header h1 {
  flex: 1;
  margin: 0 32px 0 0;
  text-align: center;
  color: #e7bd92;
  font-size: 20px;
  line-height: 1.4;
}
.back-button {
  width: 32px;
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 22px;
}
.withdraw-main {
  padding: 16px;
}
.history-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}
.text-button {
  border: 0;
  padding: 8px;
  background: transparent;
  color: #e7bd92;
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
}
.amount-card {
  padding: 18px;
  border: 1px solid #665746;
  border-radius: 14px;
  background: #303030;
  font-size: 16px;
}
.amount-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 18px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #76634f;
}
.currency-symbol {
  flex-shrink: 0;
  color: #e7bd92;
  font-size: 24px;
}
.amount-row input {
  flex: 1;
  width: 0;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 28px;
  line-height: 1.5;
}
.balance-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  line-height: 1.6;
}
.balance-row strong {
  color: #edc59e;
  overflow-wrap: anywhere;
}
.limits {
  margin-top: 18px;
  color: #d2c8bc;
  font-size: 14px;
  line-height: 1.9;
  overflow-wrap: anywhere;
}
.notice {
  color: #ffc2a9;
  font-size: 14px;
  line-height: 1.6;
}
.reference-note {
  color: #c6bfb6;
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 0;
}
.submit-button {
  margin-top: 24px;
  height: 48px;
  border: 0;
  border-radius: 24px;
  color: #29221c;
  background: linear-gradient(90deg, #f1d2b1, #cf9c69);
  font-size: 17px;
  font-weight: bold;
}
.retry-button {
  display: block;
  margin: 12px auto;
}
button:disabled {
  opacity: .5;
  cursor: default;
}
</style>